import { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { getKeycloakUserIdOrNull } from '@/utils/keycloakUidForSupabase';

/**
 * Persister customizado para salvar o cache do React Query no localStorage
 * Compatível com React Query v5
 * Inclui verificação de user_id para segurança multi-usuário (Keycloak)
 */
export function createLocalStoragePersister(options: { 
  key?: string;
  throttleTime?: number;
} = {}): Persister {
  const { key = 'MUSA_PORTAL_CACHE', throttleTime = 1000 } = options;

  const getCurrentUserId = async (): Promise<string | null> => {
    return getKeycloakUserIdOrNull();
  };

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        // Excluir queries grandes do cache persistido para evitar QuotaExceededError
        // Queries de impacto podem ter milhares de registros e exceder o limite do localStorage
        const filteredQueries = (client.clientState?.queries || []).filter(query => {
          const queryKey = query.queryKey;
          if (Array.isArray(queryKey) && queryKey.length > 0) {
            const firstKey = queryKey[0];
            // Excluir impact-metrics do cache persistido
            if (typeof firstKey === 'string' && firstKey.includes('impact-metrics')) {
              return false;
            }
          }
          return true;
        });

        // Criar client filtrado sem queries grandes
        const filteredClient: PersistedClient = {
          ...client,
          clientState: {
            ...client.clientState,
            queries: filteredQueries,
          }
        };

        // Inclui user_id atual no cache para validação futura
        const currentUserId = await getCurrentUserId();
        const cacheWithUserId = {
          ...filteredClient,
          metadata: {
            ...(filteredClient as any).metadata,
            userId: currentUserId,
            timestamp: Date.now(),
          }
        };
        
        const serialized = JSON.stringify(cacheWithUserId);
        
        // Verificar tamanho antes de salvar (limite de ~5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (serialized.length > maxSize) {
          console.warn('⚠️ Cache muito grande, salvando apenas metade das queries');
          // Se ainda for muito grande, salvar apenas queries essenciais
          const essentialQueries = filteredQueries.slice(0, Math.floor(filteredQueries.length / 2));
          const essentialClient: PersistedClient = {
            ...filteredClient,
            clientState: {
              ...filteredClient.clientState,
              queries: essentialQueries,
            }
          };
          const essentialCache = {
            ...essentialClient,
            metadata: {
              ...(essentialClient as any).metadata,
              userId: currentUserId,
              timestamp: Date.now(),
            }
          };
          localStorage.setItem(key, JSON.stringify(essentialCache));
        } else {
          localStorage.setItem(key, serialized);
        }
      } catch (error) {
        // Se ainda assim der erro de quota, tentar limpar queries antigas
        if (error instanceof Error && error.name === 'QuotaExceededError') {
          console.warn('⚠️ Quota excedida, limpando cache antigo e tentando novamente');
          try {
            localStorage.removeItem(key);
            // Tentar salvar apenas metadata
            const minimalCache = {
              clientState: {
                queries: [],
                mutations: [],
              },
              metadata: {
                userId: await getCurrentUserId(),
                timestamp: Date.now(),
              }
            };
            localStorage.setItem(key, JSON.stringify(minimalCache));
          } catch (retryError) {
            console.error('Erro ao salvar cache mesmo após limpeza:', retryError);
          }
        } else {
          console.error('Erro ao salvar cache:', error);
        }
      }
    },
    
    restoreClient: async () => {
      try {
        const cached = localStorage.getItem(key);
        if (!cached) return undefined;

        const parsedCache = JSON.parse(cached) as PersistedClient & {
          metadata?: { userId?: string; timestamp?: number };
        };
        
        // 🔒 SEGURANÇA: Verificar se o user_id do cache corresponde ao usuário atual
        const cachedUserId = parsedCache.metadata?.userId;
        const currentUserId = await getCurrentUserId();
        
        // Se o user_id mudou, NÃO restaurar o cache (usuário diferente)
        if (cachedUserId && currentUserId && cachedUserId !== currentUserId) {
          localStorage.removeItem(key);
          return undefined;
        }
        
        // Se não há usuário atual mas há cache com userId, limpar cache (logout/sessão expirada)
        if (!currentUserId && cachedUserId) {
          localStorage.removeItem(key);
          return undefined;
        }
        
        // Se não há usuário atual E não há userId no cache, permitir (usuário público)
        if (!currentUserId && !cachedUserId) {
          const { metadata, ...client } = parsedCache;
          return client as PersistedClient;
        }
        
        // Se há usuário e há cache com mesmo userId, permitir
        if (currentUserId && cachedUserId === currentUserId) {
          const { metadata, ...client } = parsedCache;
          return client as PersistedClient;
        }
        
        // Caso padrão: não restaurar
        return undefined;
      } catch (error) {
        console.error('Erro ao restaurar cache:', error);
        return undefined;
      }
    },
    
    removeClient: async () => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Erro ao remover cache:', error);
      }
    },
  };
}

